import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { ChartModule } from 'primeng/chart';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { type PaginatorState } from 'primeng/paginator';

import { AuthService } from '@/core/auth/auth.service';
import { MedicoEstatisticaAgrupadaResponse } from '@/core/api/sicom-api/registro/medico/controle-administrativo/medico-estatistica-agrupada.model';
import { MedicoPendenciaResponse } from '@/core/api/sicom-api/registro/medico/pendencia/medico-pendencia.model';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { MapaUfCard } from '@/shared/components/mapa-uf-card/mapa-uf-card';
import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { CardContent } from '@/shared/components/card-content/card-content';
import { EstatisticaMensalUtils } from '@/shared/utils/estatistica-mensal-utils';
import { MedicoControleAdministrativoFeatureService } from '../mapa-brasil/medico-controle-administrativo.service';
import { DetalhamentoPendencias } from '../detalhamento-pendencias/detalhamento-pendencias';
import { IndicadorPendencia } from '../mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../mapa-pendencias/pendencia-linha.model';
import { UfSelecaoGestaoService } from '../uf-selecao-gestao.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
const UFS_VALIDAS = new Set(['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']);

type EstatisticaId =
    | 'novas-inscricoes'
    | 'tempo-formatura-inscricao'
    | 'conversao-provisoria'
    | 'faixa-etaria'
    | 'genero'
    | 'municipios'
    | 'transferencias-origem-destino'
    | 'movimentacao-situacao'
    | 'situacao-cadastral'
    | 'situacao-por-mes'
    | 'tempo-situacao'
    | 'cancelamentos-situacao'
    | 'reinscricoes-cancelamento'
    | 'protocolos-medico'
    | 'emissoes-documentais'
    | 'documentos-ausentes'
    | 'cadastros-atualizacao'
    | 'cadastros-atualizados-recentes'
    | 'anotacoes-administrativas'
    | 'contatos-validos'
    | 'duplicidades-potenciais'
    | 'faculdades-formacao'
    | 'sem-especialidade'
    | 'media-especialidades'
    | 'subgrupos-especiais'
    | 'formados-exterior'
    | 'especialidades'
    | 'telemedicina-tipos'
    | 'deficiencia-tipos'
    | 'pos-graduacao-tipos'
    | 'pos-graduacao-instituicoes'
    | 'conflito-interesse-situacao'
    | 'pcmso-riscos'
    | 'vistos-vencimento-faixa'
    | 'projecao-aposentadoria'
    | 'retencao-formados'
    | 'idade-media-especialidade'
    | 'tempo-inscricao-titulo'
    | 'evolucao-anual-ativos'
    | 'interiorizacao-anual'
    | 'tempo-medio-atendimento-protocolo'
    | 'tempo-medio-tramitacao-transferencia'
    | 'isencoes-doenca'
    | 'termo-lgpd'
    | 'vistos-concedidos-tipo'
    | 'ocorrencias-tipo'
    | 'diplomas-apresentacao'
    | 'estado-civil'
    | 'tipo-sanguineo'
    | 'limite-gastos-mensal';

type EstatisticaModoTotal = 'soma' | 'primeiro' | 'ultimo' | 'media';

type EstatisticaVisualizacao = 'kpi' | 'linha' | 'rosca' | 'barra' | 'lista' | 'transferencias';

const ESTATISTICAS_MENSAIS: ReadonlySet<EstatisticaId> = new Set<EstatisticaId>(['novas-inscricoes', 'cadastros-atualizados-recentes', 'situacao-por-mes']);

const ESTATISTICAS_ANUAIS: ReadonlySet<EstatisticaId> = new Set<EstatisticaId>(['evolucao-anual-ativos', 'interiorizacao-anual']);

const ESTATISTICAS_SEM_ANALITICO: ReadonlySet<EstatisticaId> = new Set<EstatisticaId>(['genero', 'tempo-inscricao-titulo', 'evolucao-anual-ativos', 'interiorizacao-anual', 'tempo-medio-atendimento-protocolo', 'tempo-medio-tramitacao-transferencia']);

type EstatisticaGrupo = {
    id: EstatisticaId;
    titulo: string;
    descricao: string;
    grupo: string;
    classeGrupo: string;
    rotuloTotal: string;
    modoTotal: EstatisticaModoTotal;
    visualizacao: EstatisticaVisualizacao;
    linhaInteira?: boolean;
    itens: MedicoEstatisticaAgrupadaResponse[] | null;
    carregando: boolean;
};

type AgrupadorEstatistica = {
    slug: string;
    label: string;
    icon: string;
    descricao: string;
};

type SecaoEstatistica = { titulo: string; mostrarTitulo: boolean; grupos: EstatisticaGrupo[] };

type FaixaEtariaCodigo = 'Ate 29 anos' | '30 a 39 anos' | '40 a 49 anos' | '50 a 59 anos' | '60 a 69 anos' | '70 a 79 anos' | '80 anos ou mais' | 'Nao informado';

type FaixaEtariaOpcao = {
    codigo: FaixaEtariaCodigo;
    label: string;
};

type SubgrupoEspecialCodigo = 'ESTRANGEIRO' | 'MILITAR' | 'DOADOR' | 'MINISTERIO';

type SubgrupoEspecialOpcao = {
    codigo: SubgrupoEspecialCodigo;
    label: string;
};

@Component({
    selector: 'app-estatisticas-cadastro',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ButtonModule, CardModule, SelectEstado, MapaUfCard, SkeletonModule, TagModule, ChartModule, DialogModule, DetalhamentoPendencias, CardContent, PainelLateral],
    templateUrl: './estatisticas-cadastro.html',
    styleUrl: './estatisticas-cadastro.scss'
})
export class EstatisticasCadastro implements OnInit {
    // Paleta suave já usada em todo o dashboard financeiro (ver card-divida-ativa-dashboard-painel.ts,
    // card-pagamentos-dashboard-painel.ts, card-tipos-recebido-sintetico.ts) — reaproveitada aqui para
    // manter a mesma identidade visual entre os painéis de Gestão em vez de introduzir uma paleta nova.
    private static readonly PALETA_CATEGORICA_RGB: readonly (readonly [number, number, number])[] = [
        [54, 162, 235], // azul
        [75, 192, 192], // verde-água
        [255, 159, 64], // laranja
        [255, 99, 132], // rosa
        [153, 102, 255], // roxo
        [255, 205, 86], // amarelo
        [201, 203, 207] // cinza
    ];

    readonly anoAtual = new Date().getFullYear();
    readonly limiteRanking = 10;
    readonly faixaEtariaPadrao: FaixaEtariaCodigo = 'Ate 29 anos';
    readonly faixaEtariaOpcoes: FaixaEtariaOpcao[] = [
        { codigo: 'Ate 29 anos', label: 'Até 29 anos' },
        { codigo: '30 a 39 anos', label: '30 a 39 anos' },
        { codigo: '40 a 49 anos', label: '40 a 49 anos' },
        { codigo: '50 a 59 anos', label: '50 a 59 anos' },
        { codigo: '60 a 69 anos', label: '60 a 69 anos' },
        { codigo: '70 a 79 anos', label: '70 a 79 anos' },
        { codigo: '80 anos ou mais', label: '80 anos ou mais' },
        { codigo: 'Nao informado', label: 'Não informado' }
    ];
    readonly subgrupoEspecialPadrao: SubgrupoEspecialCodigo = 'ESTRANGEIRO';
    readonly subgrupoEspecialOpcoes: SubgrupoEspecialOpcao[] = [
        { codigo: 'ESTRANGEIRO', label: 'Médicos estrangeiros' },
        { codigo: 'MILITAR', label: 'Com informação militar' },
        { codigo: 'DOADOR', label: 'Doadores declarados' },
        { codigo: 'MINISTERIO', label: 'Com informação de ministério' }
    ];
    readonly agrupadores: AgrupadorEstatistica[] = [
        { slug: 'evolucao', label: 'Evolução', icon: 'pi pi-chart-line', descricao: 'Séries temporais e movimentações por período.' },
        { slug: 'perfil', label: 'Perfil', icon: 'pi pi-users', descricao: 'Distribuições demográficas e subgrupos cadastrais.' },
        { slug: 'endereco', label: 'Endereço', icon: 'pi pi-map-marker', descricao: 'Concentração territorial dos cadastros médicos.' },
        { slug: 'formacao', label: 'Formação', icon: 'pi pi-graduation-cap', descricao: 'Indicadores acadêmicos e origem da formação médica.' },
        { slug: 'registro', label: 'Registro', icon: 'pi pi-id-card', descricao: 'Conversões, reinscrições e movimentos de registro.' },
        { slug: 'transferencia', label: 'Transferência', icon: 'pi pi-arrow-right-arrow-left', descricao: 'Origem, destino e tempo de tramitação de transferências.' },
        { slug: 'situacao', label: 'Situação', icon: 'pi pi-verified', descricao: 'Movimentos e tempos por situação cadastral.' },
        { slug: 'operacional', label: 'Operacional', icon: 'pi pi-briefcase', descricao: 'Protocolos, emissões e anotações administrativas.' },
        { slug: 'qualidade', label: 'Qualidade', icon: 'pi pi-check-circle', descricao: 'Completude, atualização e consistência de dados.' },
        { slug: 'especialidade', label: 'Especialidade', icon: 'pi pi-star', descricao: 'Cobertura e distribuição de especialidades registradas.' },
        { slug: 'vinculos', label: 'Vínculos', icon: 'pi pi-link', descricao: 'Conflitos de interesse e responsabilidade técnica (PCMSO).' }
    ];
    anoSelecionado = this.anoAtual;
    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;
    grupoSelecionadoSlug = this.agrupadores[0].slug;
    grupoMaximizado: EstatisticaGrupo | null = null;
    exibirModalMaximizados = false;
    estatisticaSelecionada: EstatisticaGrupo | null = null;
    grupoAnaliticoCodigo: string | null = null;
    linhasAnalitico: PendenciaLinha[] = [];
    totalRegistrosAnalitico = 0;
    firstAnalitico = 0;
    rowsAnalitico = 100;
    carregandoAnalitico = false;
    faixaEtariaSelecionada: FaixaEtariaCodigo = this.faixaEtariaPadrao;
    subgrupoEspecialSelecionado: SubgrupoEspecialCodigo = this.subgrupoEspecialPadrao;

    estatisticas: EstatisticaGrupo[] = [
        {
            id: 'novas-inscricoes',
            titulo: 'Novas inscrições por mês',
            descricao: 'Registros de inscrição principal no ano selecionado.',
            grupo: 'Evolução',
            classeGrupo: 'grupo-evolucao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'linha',
            itens: null,
            carregando: false
        },
        {
            id: 'tempo-formatura-inscricao',
            titulo: 'Tempo médio formatura/inscrição',
            descricao: 'Média em meses entre formatura da graduação e primeira inscrição.',
            grupo: 'Formação',
            classeGrupo: 'grupo-formacao',
            rotuloTotal: 'meses',
            modoTotal: 'primeiro',
            visualizacao: 'kpi',
            itens: null,
            carregando: false
        },
        {
            id: 'conversao-provisoria',
            titulo: 'Conversão de inscrição provisória',
            descricao: 'Inscrições provisórias convertidas ou sem conversão definitiva.',
            grupo: 'Registro',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'faixa-etaria',
            titulo: 'Distribuição por faixa etária',
            descricao: 'Médicos cadastrados agrupados por idade.',
            grupo: 'Perfil',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'genero',
            titulo: 'Distribuição por gênero',
            descricao: 'Médicos cadastrados por gênero informado.',
            grupo: 'Perfil',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'municipios',
            titulo: 'Médicos por município',
            descricao: 'Maiores bases por município de correspondência.',
            grupo: 'Endereço',
            classeGrupo: 'grupo-endereco',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'transferencias-origem-destino',
            titulo: 'Transferências origem/destino',
            descricao: 'Ranking de transferências por UF de registro e UF de origem/destino.',
            grupo: 'Transferência',
            classeGrupo: 'grupo-transferencia',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'transferencias',
            linhaInteira: true,
            itens: null,
            carregando: false
        },

        {
            id: 'movimentacao-situacao',
            titulo: 'Movimentação de situação cadastral',
            descricao: 'Movimentos do histórico cadastral por modo de inscrição no ano selecionado.',
            grupo: 'Situação',
            classeGrupo: 'grupo-situacao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'situacao-cadastral',
            titulo: 'Total por situação',
            descricao: 'Médicos agrupados pela situação cadastral atual.',
            grupo: 'Situação',
            classeGrupo: 'grupo-situacao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'situacao-por-mes',
            titulo: 'Situação por mês',
            descricao: 'Movimentações de situação por mês no ano selecionado.',
            grupo: 'Situação',
            classeGrupo: 'grupo-situacao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'tempo-situacao',
            titulo: 'Tempo médio por situação',
            descricao: 'Média de dias em ciclos encerrados de cada modo de inscrição.',
            grupo: 'Situação',
            classeGrupo: 'grupo-situacao',
            rotuloTotal: 'dias',
            modoTotal: 'media',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'cancelamentos-situacao',
            titulo: 'Cancelamentos por situação',
            descricao: 'Cancelamentos do ano agrupados pelo modo de inscrição registrado.',
            grupo: 'Situação',
            classeGrupo: 'grupo-situacao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'reinscricoes-cancelamento',
            titulo: 'Reinscrições após cancelamento',
            descricao: 'Médicos cancelados no ano com ou sem reinscrição principal posterior.',
            grupo: 'Registro',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'protocolos-medico',
            titulo: 'Protocolos ligados ao médico',
            descricao: 'Tipos de protocolo vinculados a médicos no ano selecionado.',
            grupo: 'Operacional',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'protocolos',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'emissoes-documentais',
            titulo: 'Emissões documentais',
            descricao: 'Protocolos de carteira, cédula e certidão vinculados a médicos.',
            grupo: 'Operacional',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'protocolos',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'documentos-ausentes',
            titulo: 'Documentos ausentes',
            descricao: 'Ocorrências de médicos sem foto, assinatura ou digital cadastradas.',
            grupo: 'Qualidade',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'ocorrências',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'cadastros-atualizacao',
            titulo: 'Atualização cadastral',
            descricao: 'Distribuição pela última atualização registrada no médico, solicitante ou endereço.',
            grupo: 'Qualidade',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'cadastros-atualizados-recentes',
            titulo: 'Atualizações por mês',
            descricao: 'Cadastros com atualização mais recente em cada mês do ano selecionado.',
            grupo: 'Qualidade',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'linha',
            itens: null,
            carregando: false
        },
        {
            id: 'anotacoes-administrativas',
            titulo: 'Anotações administrativas',
            descricao: 'Médicos com anotações por tipo de referência no ano selecionado.',
            grupo: 'Operacional',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'médicos',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'contatos-validos',
            titulo: 'Contatos válidos',
            descricao: 'Médicos com e-mail, telefone ou endereço de correspondência em formato utilizável.',
            grupo: 'Qualidade',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'ocorrências',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'duplicidades-potenciais',
            titulo: 'Duplicidades potenciais',
            descricao: 'Registros envolvidos em possíveis duplicidades por CPF.',
            grupo: 'Qualidade',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'faculdades-formacao',
            titulo: 'Faculdades de formação',
            descricao: 'Principais faculdades de graduação informadas no cadastro médico.',
            grupo: 'Formação',
            classeGrupo: 'grupo-formacao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'sem-especialidade',
            titulo: 'Cobertura de especialidade',
            descricao: 'Médicos com e sem especialidade ativa registrada.',
            grupo: 'Especialidade',
            classeGrupo: 'grupo-especialidade',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'media-especialidades',
            titulo: 'Média de especialidades',
            descricao: 'Média arredondada de especialidades ativas por médico.',
            grupo: 'Especialidade',
            classeGrupo: 'grupo-especialidade',
            rotuloTotal: 'especialidades por médico',
            modoTotal: 'primeiro',
            visualizacao: 'kpi',
            itens: null,
            carregando: false
        },
        {
            id: 'subgrupos-especiais',
            titulo: 'Subgrupos especiais',
            descricao: 'Médicos estrangeiros, com informação militar, doadores e com dados de ministério.',
            grupo: 'Perfil',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'ocorrências',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'formados-exterior',
            titulo: 'Formados no exterior por país',
            descricao: 'Principais países de formação médica no exterior.',
            grupo: 'Formação',
            classeGrupo: 'grupo-formacao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'especialidades',
            titulo: 'Especialidades mais registradas',
            descricao: 'Especialidades com maior volume de registros.',
            grupo: 'Especialidade',
            classeGrupo: 'grupo-especialidade',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'telemedicina-tipos',
            titulo: 'Médicos por tipo de telemedicina',
            descricao: 'Médicos agrupados pelo tipo de telemedicina informado.',
            grupo: 'Registro',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'deficiencia-tipos',
            titulo: 'Médicos por tipo de deficiência',
            descricao: 'Médicos agrupados pelo tipo de deficiência informado.',
            grupo: 'Perfil',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'pos-graduacao-tipos',
            titulo: 'Pós-graduações por tipo de diploma',
            descricao: 'Pós-graduações agrupadas pelo tipo de diploma.',
            grupo: 'Formação',
            classeGrupo: 'grupo-formacao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'pos-graduacao-instituicoes',
            titulo: 'Instituições de pós-graduação',
            descricao: 'Principais instituições de pós-graduação informadas.',
            grupo: 'Formação',
            classeGrupo: 'grupo-formacao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'conflito-interesse-situacao',
            titulo: 'Conflitos de interesse por situação',
            descricao: 'Conflitos de interesse agrupados pela situação.',
            grupo: 'Vínculos',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'pcmso-riscos',
            titulo: 'PCMSO por tipo de risco',
            descricao: 'Responsabilidade técnica de PCMSO agrupada por tipo de risco.',
            grupo: 'Vínculos',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'vistos-vencimento-faixa',
            titulo: 'Vistos provisórios por faixa de vencimento',
            descricao: 'Vistos provisórios agrupados pela faixa de vencimento (vencidos e a vencer em 30/60/90 dias).',
            grupo: 'Registro',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'projecao-aposentadoria',
            titulo: 'Projeção de aposentadoria',
            descricao: 'Médicos por faixa etária próxima da aposentadoria (60 a 64, 65 a 69 e 70 ou mais).',
            grupo: 'Perfil',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'retencao-formados',
            titulo: 'Retenção de formados',
            descricao: 'Médicos formados na própria UF que permanecem registrados frente aos vindos de outra UF.',
            grupo: 'Formação',
            classeGrupo: 'grupo-formacao',
            rotuloTotal: 'registros',
            modoTotal: 'soma',
            visualizacao: 'rosca',
            itens: null,
            carregando: false
        },
        {
            id: 'idade-media-especialidade',
            titulo: 'Idade média por especialidade',
            descricao: 'Ranking das especialidades pela idade média dos médicos registrados. O destaque é a média das 10 especialidades listadas, não a média nacional.',
            grupo: 'Especialidade',
            classeGrupo: 'grupo-especialidade',
            rotuloTotal: 'anos',
            modoTotal: 'media',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'tempo-inscricao-titulo',
            titulo: 'Tempo entre inscrição e título',
            descricao: 'Média em meses entre a inscrição principal e a emissão do título de especialista. O destaque é a média das 10 especialidades listadas, não a média nacional.',
            grupo: 'Especialidade',
            classeGrupo: 'grupo-especialidade',
            rotuloTotal: 'meses',
            modoTotal: 'media',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'evolucao-anual-ativos',
            titulo: 'Evolução anual de ativos',
            descricao: 'Total de médicos ativos ao final de cada ano. O destaque mostra o ano mais recente.',
            grupo: 'Evolução',
            classeGrupo: 'grupo-evolucao',
            rotuloTotal: 'ativos',
            modoTotal: 'ultimo',
            visualizacao: 'linha',
            itens: null,
            carregando: false
        },
        {
            id: 'interiorizacao-anual',
            titulo: 'Interiorização anual',
            descricao: 'Médicos que passaram a ter endereço no interior em cada ano. O destaque mostra o ano mais recente.',
            grupo: 'Endereço',
            classeGrupo: 'grupo-endereco',
            rotuloTotal: 'no interior',
            modoTotal: 'ultimo',
            visualizacao: 'linha',
            itens: null,
            carregando: false
        },
        {
            id: 'tempo-medio-atendimento-protocolo',
            titulo: 'Tempo médio de atendimento de protocolo',
            descricao: 'Média de dias para atendimento dos protocolos por tipo no ano selecionado.',
            grupo: 'Operacional',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'dias',
            modoTotal: 'media',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'tempo-medio-tramitacao-transferencia',
            titulo: 'Tempo médio de tramitação de transferência',
            descricao: 'Média de dias na tramitação das transferências por etapa.',
            grupo: 'Transferência',
            classeGrupo: 'grupo-transferencia',
            rotuloTotal: 'dias',
            modoTotal: 'media',
            visualizacao: 'barra',
            itens: null,
            carregando: false
        },
        {
            id: 'isencoes-doenca',
            titulo: 'Isenções por doença/desemprego',
            descricao: 'Médicos com isenção de anuidade vigente por motivo (desemprego, auxílio-doença ou limitação laboral).',
            grupo: 'Perfil',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'médicos',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'termo-lgpd',
            titulo: 'Adesão ao Termo LGPD',
            descricao: 'Médicos ativos com e sem termo LGPD aceito.',
            grupo: 'Qualidade',
            classeGrupo: 'grupo-qualidade',
            rotuloTotal: 'médicos',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'vistos-concedidos-tipo',
            titulo: 'Vistos concedidos por modalidade',
            descricao: 'Vistos concedidos agrupados entre fracionado e integral (corrido).',
            grupo: 'Registro',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'vistos',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'ocorrencias-tipo',
            titulo: 'Ocorrências por tipo',
            descricao: 'Ranking de ocorrências administrativas registradas por tipo.',
            grupo: 'Operacional',
            classeGrupo: 'grupo-registro',
            rotuloTotal: 'ocorrências',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'diplomas-apresentacao',
            titulo: 'Apresentação de diploma',
            descricao: 'Formações de médicos ativos agrupadas por apresentação do diploma.',
            grupo: 'Formação',
            classeGrupo: 'grupo-formacao',
            rotuloTotal: 'formações',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'estado-civil',
            titulo: 'Distribuição por estado civil',
            descricao: 'Médicos ativos agrupados pelo estado civil informado.',
            grupo: 'Perfil',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'médicos',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        },
        {
            id: 'tipo-sanguineo',
            titulo: 'Distribuição por tipo sanguíneo',
            descricao: 'Médicos ativos agrupados pelo tipo sanguíneo informado.',
            grupo: 'Perfil',
            classeGrupo: 'grupo-perfil',
            rotuloTotal: 'médicos',
            modoTotal: 'soma',
            visualizacao: 'lista',
            itens: null,
            carregando: false
        }
    ];

    private ufTokenRestrita: string | null = null;
    private ufConsulta: string | null = null;
    private atualizacaoId = 0;
    private readonly ufChange$ = new Subject<void>();
    private readonly analiticoChange$ = new Subject<void>();

    constructor(
        private medicoControleAdministrativoService: MedicoControleAdministrativoFeatureService,
        private destroyRef: DestroyRef,
        private authService: AuthService,
        private route: ActivatedRoute,
        private messageService: MessageService,
        private ufSelecaoGestao: UfSelecaoGestaoService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.configurarUfPorToken();
        // A rota dispara a primeira carga: paramMap emite o valor corrente na inscricao.
        this.observarAgrupadorRota();
    }

    obterAgrupadorSelecionado(): AgrupadorEstatistica {
        return this.agrupadores.find((agrupador) => agrupador.slug === this.grupoSelecionadoSlug) ?? this.agrupadores[0];
    }

    obterEstatisticasGrupo(): EstatisticaGrupo[] {
        const agrupador = this.obterAgrupadorSelecionado();
        return this.estatisticas.filter((grupo) => grupo.grupo === agrupador.label);
    }

    private readonly ordemSecoes: { titulo: string; tipos: EstatisticaVisualizacao[] }[] = [
        { titulo: 'Indicadores-resumo', tipos: ['kpi'] },
        { titulo: 'Séries e evolução', tipos: ['linha'] },
        { titulo: 'Distribuições', tipos: ['rosca', 'barra', 'transferencias'] },
        { titulo: 'Detalhamento', tipos: ['lista'] }
    ];

    private secoesCache: { slug: string; estatisticas: EstatisticaGrupo[]; secoes: SecaoEstatistica[] } | null = null;

    obterSecoesEstatisticas(): SecaoEstatistica[] {
        // `this.estatisticas` recebe uma NOVA referência de array (e de cada grupo) a cada
        // resposta de consulta (ver atualizarGrupo/definirCarregando/resetarGrupos), então a
        // invalidação do cache precisa considerar essa referência além do slug selecionado.
        if (this.secoesCache && this.secoesCache.slug === this.grupoSelecionadoSlug && this.secoesCache.estatisticas === this.estatisticas) {
            return this.secoesCache.secoes;
        }

        const estatisticas = this.obterEstatisticasGrupo();
        const secoes = this.ordemSecoes
            .map((s) => ({
                titulo: s.titulo,
                mostrarTitulo: true,
                grupos: estatisticas.filter((g) => s.tipos.includes(g.visualizacao))
            }))
            .filter((s) => s.grupos.length > 0);
        // 1 seção só → sem cabeçalho (aparência atual)
        const resultado = secoes.length <= 1 ? secoes.map((s) => ({ ...s, mostrarTitulo: false })) : secoes;

        this.secoesCache = { slug: this.grupoSelecionadoSlug, estatisticas: this.estatisticas, secoes: resultado };
        return resultado;
    }

    maximizarGrupo(grupo: EstatisticaGrupo): void {
        this.grupoMaximizado = grupo;
        this.exibirModalMaximizados = true;
    }

    possuiAnalitico(grupo: EstatisticaGrupo): boolean {
        return !ESTATISTICAS_SEM_ANALITICO.has(grupo.id) && this.obterOpcoesGrupoAnalitico(grupo).length > 0;
    }

    selecionarEstatisticaAnalitica(grupo: EstatisticaGrupo): void {
        const primeiraOpcao = this.obterOpcoesGrupoAnalitico(grupo)[0];
        if (!primeiraOpcao?.codigo || !this.possuiAnalitico(grupo)) {
            return;
        }

        this.analiticoChange$.next();
        this.estatisticaSelecionada = grupo;
        if (grupo.id === 'faixa-etaria') {
            this.faixaEtariaSelecionada = this.faixaEtariaPadrao;
            this.grupoAnaliticoCodigo = this.faixaEtariaPadrao;
        } else if (grupo.id === 'subgrupos-especiais') {
            this.subgrupoEspecialSelecionado = this.subgrupoEspecialPadrao;
            this.grupoAnaliticoCodigo = this.subgrupoEspecialPadrao;
        } else {
            this.grupoAnaliticoCodigo = primeiraOpcao.codigo;
        }
        this.firstAnalitico = 0;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.carregarAnaliticoEstatistica();
    }

    voltarParaEstatisticas(): void {
        this.analiticoChange$.next();
        this.estatisticaSelecionada = null;
        this.grupoAnaliticoCodigo = null;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.firstAnalitico = 0;
        this.carregandoAnalitico = false;
    }

    onGrupoAnaliticoChange(codigo: string): void {
        this.analiticoChange$.next();
        this.grupoAnaliticoCodigo = codigo;

        if (this.estatisticaSelecionada?.id === 'faixa-etaria') {
            this.faixaEtariaSelecionada = this.normalizarFaixaEtaria(codigo);
            this.grupoAnaliticoCodigo = this.faixaEtariaSelecionada;
        }

        if (this.estatisticaSelecionada?.id === 'subgrupos-especiais') {
            this.subgrupoEspecialSelecionado = this.normalizarSubgrupoEspecial(codigo);
            this.grupoAnaliticoCodigo = this.subgrupoEspecialSelecionado;
        }

        this.firstAnalitico = 0;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.carregarAnaliticoEstatistica();
    }

    onPageChangeAnalitico(event: PaginatorState): void {
        this.analiticoChange$.next();
        this.firstAnalitico = event.first ?? 0;
        this.rowsAnalitico = event.rows ?? this.rowsAnalitico;
        this.carregarAnaliticoEstatistica();
    }

    onFaixaEtariaAnaliticoChange(valor: string): void {
        this.onGrupoAnaliticoChange(valor);
    }

    onSubgrupoEspecialAnaliticoChange(valor: string): void {
        this.onGrupoAnaliticoChange(valor);
    }

    obterOpcoesGrupoAnalitico(grupo: EstatisticaGrupo | null = this.estatisticaSelecionada): MedicoEstatisticaAgrupadaResponse[] {
        if (grupo?.id === 'faixa-etaria') {
            return this.faixaEtariaOpcoes.map((opcao, index) => ({ codigo: opcao.codigo, descricao: opcao.label, ordem: index + 1, total: 1 }));
        }

        if (grupo?.id === 'subgrupos-especiais') {
            return this.subgrupoEspecialOpcoes.map((opcao, index) => ({ codigo: opcao.codigo, descricao: opcao.label, ordem: index + 1, total: 1 }));
        }

        return (grupo?.itens ?? []).filter((item) => !!item.codigo && (item.total ?? 0) > 0);
    }

    obterIndicadorAnalitico(): IndicadorPendencia | null {
        if (!this.estatisticaSelecionada) {
            return null;
        }

        const grupoSelecionado = this.obterGrupoAnaliticoSelecionado();
        return {
            id: this.estatisticaSelecionada.id,
            titulo: this.estatisticaSelecionada.titulo,
            descricao: grupoSelecionado ? `${this.estatisticaSelecionada.descricao} Grupo: ${this.obterDescricaoItem(grupoSelecionado)}.` : this.estatisticaSelecionada.descricao,
            grupo: this.estatisticaSelecionada.grupo as IndicadorPendencia['grupo'],
            classeGrupo: this.estatisticaSelecionada.classeGrupo,
            quantidade: this.totalRegistrosAnalitico || null
        };
    }

    obterColunaDetalheAnalitico(): string | null {
        if (this.estatisticaSelecionada?.id === 'novas-inscricoes') {
            return 'Mês de inscrição';
        }

        if (this.estatisticaSelecionada?.id === 'subgrupos-especiais') {
            return 'Subgrupo';
        }

        if (this.estatisticaSelecionada?.id === 'situacao-por-mes') {
            return 'Mês da situação';
        }
        if (this.estatisticaSelecionada?.id === 'situacao-cadastral') {
            return 'Situação';
        }
        return this.estatisticaSelecionada ? 'Grupo selecionado' : null;
    }

    obterCorGrupo(_grupo: string): string {
        return getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || '#3b82f6';
    }

    /** Cor de fundo do card (mesma usada em --gc-card-bg no SCSS) — usada como borda dos gráficos
     * de rosca para abrir um respiro de 2px entre fatias, em vez de fatias coladas. */
    private obterCorFundoCard(): string {
        const isDark = document.documentElement.classList.contains('dark') || document.documentElement.classList.contains('app-dark');
        return isDark ? '#111827' : '#ffffff';
    }

    /**
     * Paleta categórica suave (mesma do dashboard financeiro) para gráficos de rosca com
     * categorias nominais (sem ordem/hierarquia entre elas, ex.: gênero, tipo de deficiência).
     * `alpha` controla o preenchimento (0.6, padrão) vs. o hover/traço (1, opaco). Cicla se
     * totalItens > 7.
     */
    gerarCoresCategoricas(totalItens: number, alpha = 0.6): string[] {
        return Array.from({ length: totalItens }, (_, indice) => {
            const [r, g, b] = EstatisticasCadastro.PALETA_CATEGORICA_RGB[indice % EstatisticasCadastro.PALETA_CATEGORICA_RGB.length];
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        });
    }

    onUfSelecionadaChange(uf?: string | null): void {
        this.ufSelecionada = this.normalizarUfSelecionada(uf);
        this.ufSelecaoGestao.definir(this.ufSelecionada);
        this.atualizarEstatisticas();
    }

    aplicarFiltros(): void {
        this.anoSelecionado = this.normalizarAno(this.anoSelecionado);
        this.atualizarEstatisticas();
    }

    obterTotalGrupo(grupo: EstatisticaGrupo): number | null {
        if (grupo.itens === null) {
            return null;
        }

        if (grupo.modoTotal === 'primeiro') {
            return grupo.itens[0]?.total ?? 0;
        }

        // Series anuais/mensais chegam em ordem crescente: o destaque e o periodo mais recente.
        if (grupo.modoTotal === 'ultimo') {
            return grupo.itens[grupo.itens.length - 1]?.total ?? 0;
        }

        if (grupo.modoTotal === 'media') {
            if (!grupo.itens.length) {
                return 0;
            }

            const soma = grupo.itens.reduce((total, item) => total + (item.total ?? 0), 0);
            return Math.round(soma / grupo.itens.length);
        }

        return grupo.itens.reduce((total, item) => total + (item.total ?? 0), 0);
    }

    obterDescricaoItem(item: MedicoEstatisticaAgrupadaResponse): string {
        return item.descricao?.trim() || item.codigo?.trim() || 'Não informado';
    }

    isSpeedometer(grupo: EstatisticaGrupo): boolean {
        return grupo.id === 'limite-gastos-mensal';
    }

    obterInfoVelocimetro(grupo: EstatisticaGrupo): { valorCentro: string; rotulo: string; subtitulo: string } | null {
        if (!grupo.itens?.length) {
            return null;
        }
        const total = grupo.itens.reduce((sum, item) => sum + (item.total ?? 0), 0);
        if (total === 0) {
            return { valorCentro: '0%', rotulo: '', subtitulo: 'Sem registros' };
        }
        const itemDestaque = grupo.itens[0];
        const valorDestaque = itemDestaque.total ?? 0;
        const porcentagem = Math.round((valorDestaque / total) * 100);

        if (grupo.id === 'limite-gastos-mensal') {
            return {
                valorCentro: `$${valorDestaque.toLocaleString('en-US')}`,
                rotulo: `Spent of $${total.toLocaleString('en-US')}`,
                subtitulo: ''
            };
        }

        const valorFormatado = valorDestaque.toLocaleString('pt-BR');
        const rotulo = this.obterDescricaoItem(itemDestaque);
        const subtitulo = `de ${total.toLocaleString('pt-BR')} ${grupo.rotuloTotal || 'registros'}`;
        return {
            valorCentro: `${porcentagem}%`,
            rotulo,
            subtitulo
        };
    }

    obterComplementoItem(item: MedicoEstatisticaAgrupadaResponse, grupo?: EstatisticaGrupo): string {
        // Esses grupos têm `codigo` como chave interna do enum (ex.: "DESEMPREGADO"), redundante
        // com a `descricao` já exibida como título da linha — exibi-la só duplicaria a informação.
        if (grupo?.id === 'subgrupos-especiais' || grupo?.id === 'documentos-ausentes' || grupo?.id === 'isencoes-doenca') {
            return '';
        }
        const codigo = item.codigo?.trim() ?? '';
        const descricao = item.descricao?.trim() ?? '';
        const partes: string[] = [];

        if (item.ufSgUf && !this.ufSelecionada) {
            partes.push(item.ufSgUf);
        }

        if (codigo && codigo !== descricao) {
            partes.push(codigo);
        }

        return partes.join(' - ');
    }

    obterLarguraBarra(grupo: EstatisticaGrupo, item: MedicoEstatisticaAgrupadaResponse): string {
        const total = item.total ?? 0;
        const maiorTotal = this.obterMaiorTotal(grupo);

        if (!total || !maiorTotal) {
            return '0%';
        }

        return `${Math.max(6, Math.round((total / maiorTotal) * 100))}%`;
    }

    obterTipoGrafico(grupo: EstatisticaGrupo): 'line' | 'bar' | 'doughnut' {
        if (grupo.visualizacao === 'linha') {
            return 'line';
        }

        if (grupo.visualizacao === 'rosca') {
            return 'doughnut';
        }

        return 'bar';
    }

    obterAlturaBarraVertical(grupo: EstatisticaGrupo, item: MedicoEstatisticaAgrupadaResponse): string {
        const total = item.total ?? 0;
        const maiorTotal = this.obterMaiorTotal(grupo);

        if (!total || !maiorTotal) {
            return '0%';
        }

        return `${Math.max(8, Math.round((total / maiorTotal) * 100))}%`;
    }

    obterUfTransferencia(item: MedicoEstatisticaAgrupadaResponse): string {
        const uf = item.ufSgUf?.trim().toUpperCase();

        if (uf && UFS_VALIDAS.has(uf)) {
            return uf;
        }

        const texto = `${item.codigo ?? ''} ${item.descricao ?? ''}`.toUpperCase();
        const ufs = (texto.match(/\b[A-Z]{2}\b/g) ?? []).filter((sigla) => UFS_VALIDAS.has(sigla));
        return ufs[ufs.length - 1] ?? '';
    }

    obterBandeiraUf(uf?: string | null): string | null {
        const sigla = (uf ?? '').trim().toUpperCase();

        if (!UFS_VALIDAS.has(sigla)) {
            return null;
        }

        return `images/estados/${sigla}.png`;
    }

    private graficoCache = new Map<
        EstatisticaId,
        {
            itens: unknown;
            isDark: boolean;
            dados?: any;
            opcoesCard?: any;
            opcoesModal?: any;
        }
    >();

    private obterEntradaCacheGrafico(grupo: EstatisticaGrupo) {
        const isDark = document.documentElement.classList.contains('dark') || document.documentElement.classList.contains('app-dark');
        let cache = this.graficoCache.get(grupo.id);
        if (!cache || cache.itens !== grupo.itens || cache.isDark !== isDark) {
            cache = { itens: grupo.itens, isDark };
            this.graficoCache.set(grupo.id, cache);
        }
        return cache;
    }

    obterDadosGrafico(grupo: EstatisticaGrupo): any {
        if (!grupo?.itens?.length) {
            return null;
        }
        const cache = this.obterEntradaCacheGrafico(grupo);
        if (cache.dados === undefined) {
            cache.dados = this.computarDadosGrafico(grupo);
        }
        return cache.dados;
    }

    obterOpcoesGrafico(grupo: EstatisticaGrupo, isModal = false): any {
        const cache = this.obterEntradaCacheGrafico(grupo);
        if (isModal) {
            if (cache.opcoesModal === undefined) {
                cache.opcoesModal = this.computarOpcoesGrafico(grupo, true);
            }
            return cache.opcoesModal;
        }
        if (cache.opcoesCard === undefined) {
            cache.opcoesCard = this.computarOpcoesGrafico(grupo, false);
        }
        return cache.opcoesCard;
    }

    private computarDadosGrafico(grupo: EstatisticaGrupo): any {
        if (!grupo.itens?.length) {
            return null;
        }

        const labels = grupo.itens.map((item) => this.obterDescricaoItem(item));
        const data = grupo.itens.map((item) => item.total ?? 0);

        if (this.isSpeedometer(grupo)) {
            const corBase = this.obterCorGrupo(grupo.grupo);
            const isDark = document.documentElement.classList.contains('dark') || document.documentElement.classList.contains('app-dark');
            const corFundo = isDark ? '#334155' : '#cbd5e1';

            const itemDestaque = grupo.itens[0];
            const total = grupo.itens.reduce((sum, item) => sum + (item.total ?? 0), 0);
            const valorDestaque = itemDestaque.total ?? 0;
            const valorRestante = Math.max(0, total - valorDestaque);

            return {
                labels: [this.obterDescricaoItem(itemDestaque), 'Outros'],
                datasets: [
                    {
                        data: [valorDestaque, valorRestante],
                        backgroundColor: [corBase, corFundo],
                        hoverBackgroundColor: [corBase, corFundo],
                        borderWidth: 0
                    }
                ]
            };
        }

        if (ESTATISTICAS_ANUAIS.has(grupo.id)) {
            const cor = this.obterCorGrupo(grupo.grupo);
            const ordenados = [...grupo.itens].sort((a, b) => Number(a.codigo ?? 0) - Number(b.codigo ?? 0));
            return {
                labels: ordenados.map((item) => this.obterDescricaoItem(item)),
                datasets: [
                    {
                        label: grupo.rotuloTotal,
                        data: ordenados.map((item) => item.total ?? 0),
                        fill: false,
                        borderColor: cor,
                        tension: 0.4
                    }
                ]
            };
        }

        if (grupo.id === 'novas-inscricoes' || grupo.id === 'cadastros-atualizados-recentes' || grupo.id === 'situacao-por-mes') {
            const cor = this.obterCorGrupo(grupo.grupo);
            return {
                labels,
                datasets: [
                    {
                        label: grupo.id === 'novas-inscricoes' ? 'Novas Inscrições' : 'Atualizações',
                        data,
                        fill: false,
                        borderColor: cor,
                        tension: 0.4
                    }
                ]
            };
        }

        if (grupo.id === 'genero') {
            const backgroundColors = this.gerarCoresCategoricas(data.length);

            return {
                labels,
                datasets: [
                    {
                        data,
                        backgroundColor: backgroundColors,
                        hoverBackgroundColor: this.gerarCoresCategoricas(data.length, 1),
                        borderWidth: 2,
                        borderColor: this.obterCorFundoCard()
                    }
                ]
            };
        }

        if (
            grupo.id === 'conversao-provisoria' ||
            grupo.id === 'contatos-validos' ||
            grupo.id === 'cadastros-atualizacao' ||
            grupo.id === 'sem-especialidade' ||
            grupo.id === 'reinscricoes-cancelamento' ||
            grupo.id === 'emissoes-documentais' ||
            grupo.id === 'situacao-cadastral' ||
            grupo.id === 'telemedicina-tipos' ||
            grupo.id === 'deficiencia-tipos' ||
            grupo.id === 'pos-graduacao-tipos' ||
            grupo.id === 'conflito-interesse-situacao' ||
            grupo.id === 'retencao-formados' ||
            grupo.id === 'termo-lgpd' ||
            grupo.id === 'vistos-concedidos-tipo' ||
            grupo.id === 'diplomas-apresentacao' ||
            grupo.id === 'estado-civil' ||
            grupo.id === 'tipo-sanguineo'
        ) {
            const backgroundColors = this.gerarCoresCategoricas(data.length);

            return {
                labels,
                datasets: [
                    {
                        data,
                        backgroundColor: backgroundColors,
                        hoverBackgroundColor: this.gerarCoresCategoricas(data.length, 1),
                        borderWidth: 2,
                        borderColor: this.obterCorFundoCard()
                    }
                ]
            };
        }

        if (grupo.id === 'faixa-etaria' || grupo.id === 'projecao-aposentadoria') {
            const cor = this.obterCorGrupo(grupo.grupo);
            return {
                labels,
                datasets: [
                    {
                        label: 'Médicos',
                        data,
                        backgroundColor: cor,
                        borderRadius: 6
                    }
                ]
            };
        }

        if (
            grupo.id === 'formados-exterior' ||
            grupo.id === 'especialidades' ||
            grupo.id === 'municipios' ||
            grupo.id === 'faculdades-formacao' ||
            grupo.id === 'movimentacao-situacao' ||
            grupo.id === 'tempo-situacao' ||
            grupo.id === 'cancelamentos-situacao' ||
            grupo.id === 'protocolos-medico' ||
            grupo.id === 'anotacoes-administrativas' ||
            grupo.id === 'pos-graduacao-instituicoes' ||
            grupo.id === 'pcmso-riscos' ||
            grupo.id === 'vistos-vencimento-faixa' ||
            grupo.id === 'idade-media-especialidade' ||
            grupo.id === 'tempo-inscricao-titulo' ||
            grupo.id === 'tempo-medio-atendimento-protocolo' ||
            grupo.id === 'tempo-medio-tramitacao-transferencia' ||
            grupo.id === 'isencoes-doenca' ||
            grupo.id === 'ocorrencias-tipo'
        ) {
            const cor = this.obterCorGrupo(grupo.grupo);
            return {
                labels,
                datasets: [
                    {
                        label: 'Total',
                        data,
                        backgroundColor: cor,
                        borderRadius: 4
                    }
                ]
            };
        }

        return null;
    }

    private computarOpcoesGrafico(grupo: EstatisticaGrupo, isModal = false): any {
        const isDark = document.documentElement.classList.contains('dark') || document.documentElement.classList.contains('app-dark');
        const textColor = isDark ? '#f8fafc' : '#334155';
        const gridColor = isDark ? '#334155' : '#e2e8f0';

        const lineBarOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColor,
                        font: { size: isModal ? 11 : 9 }
                    },
                    grid: { display: false }
                },
                y: {
                    ticks: {
                        color: textColor,
                        font: { size: isModal ? 11 : 9 }
                    },
                    grid: { color: gridColor }
                }
            }
        };

        const horizontalBarOptions = {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            plugins: {
                legend: {
                    display: false
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColor,
                        font: { size: isModal ? 11 : 9 }
                    },
                    grid: { color: gridColor }
                },
                y: {
                    ticks: {
                        color: textColor,
                        font: { size: isModal ? 11 : 9 }
                    },
                    grid: { display: false }
                }
            }
        };

        const doughnutPieOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: isModal ? 'right' : 'bottom',
                    labels: {
                        color: textColor,
                        boxWidth: isModal ? 14 : 10,
                        padding: isModal ? 12 : 8,
                        font: { size: isModal ? 12 : 10 }
                    }
                }
            }
        };

        const speedometerOptions = {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '78%',
            rotation: -90,
            circumference: 180,
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    enabled: true,
                    callbacks: {
                        label: (context: any) => {
                            const label = context.label || '';
                            const val = context.raw || 0;
                            return ` ${label}: ${val.toLocaleString('pt-BR')}`;
                        }
                    }
                }
            }
        };

        if (this.isSpeedometer(grupo)) {
            return speedometerOptions;
        }

        if (
            grupo.id === 'novas-inscricoes' ||
            grupo.id === 'faixa-etaria' ||
            grupo.id === 'cadastros-atualizados-recentes' ||
            grupo.id === 'situacao-por-mes' ||
            grupo.id === 'projecao-aposentadoria' ||
            grupo.id === 'evolucao-anual-ativos' ||
            grupo.id === 'interiorizacao-anual'
        ) {
            return lineBarOptions;
        }

        if (
            grupo.id === 'genero' ||
            grupo.id === 'conversao-provisoria' ||
            grupo.id === 'contatos-validos' ||
            grupo.id === 'cadastros-atualizacao' ||
            grupo.id === 'sem-especialidade' ||
            grupo.id === 'reinscricoes-cancelamento' ||
            grupo.id === 'emissoes-documentais' ||
            grupo.id === 'situacao-cadastral' ||
            grupo.id === 'telemedicina-tipos' ||
            grupo.id === 'deficiencia-tipos' ||
            grupo.id === 'pos-graduacao-tipos' ||
            grupo.id === 'conflito-interesse-situacao' ||
            grupo.id === 'retencao-formados' ||
            grupo.id === 'termo-lgpd' ||
            grupo.id === 'vistos-concedidos-tipo' ||
            grupo.id === 'diplomas-apresentacao' ||
            grupo.id === 'estado-civil' ||
            grupo.id === 'tipo-sanguineo'
        ) {
            return doughnutPieOptions;
        }

        if (
            grupo.id === 'formados-exterior' ||
            grupo.id === 'especialidades' ||
            grupo.id === 'municipios' ||
            grupo.id === 'faculdades-formacao' ||
            grupo.id === 'movimentacao-situacao' ||
            grupo.id === 'tempo-situacao' ||
            grupo.id === 'cancelamentos-situacao' ||
            grupo.id === 'protocolos-medico' ||
            grupo.id === 'anotacoes-administrativas' ||
            grupo.id === 'pos-graduacao-instituicoes' ||
            grupo.id === 'pcmso-riscos' ||
            grupo.id === 'vistos-vencimento-faixa' ||
            grupo.id === 'idade-media-especialidade' ||
            grupo.id === 'tempo-inscricao-titulo' ||
            grupo.id === 'tempo-medio-atendimento-protocolo' ||
            grupo.id === 'tempo-medio-tramitacao-transferencia' ||
            grupo.id === 'isencoes-doenca' ||
            grupo.id === 'ocorrencias-tipo'
        ) {
            return horizontalBarOptions;
        }

        return null;
    }

    private atualizarEstatisticas(): void {
        this.ufChange$.next();
        this.voltarParaEstatisticas();
        this.ufConsulta = this.ufSelecionada;
        this.resetarGrupos();

        const requestId = this.iniciarAtualizacao();
        const uf = this.ufConsulta ?? undefined;

        this.carregarGrupo('novas-inscricoes', () => this.medicoControleAdministrativoService.consultarNovasInscricoesPorMes(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('tempo-formatura-inscricao', () => this.medicoControleAdministrativoService.consultarTempoMedioFormaturaPrimeiraInscricao(uf), requestId);
        this.carregarGrupo('conversao-provisoria', () => this.medicoControleAdministrativoService.consultarConversaoInscricaoProvisoria(uf), requestId);
        this.carregarGrupo('faixa-etaria', () => this.medicoControleAdministrativoService.consultarDistribuicaoFaixaEtaria(uf), requestId);
        this.carregarGrupo('genero', () => this.medicoControleAdministrativoService.consultarDistribuicaoGenero(uf), requestId);
        this.carregarGrupo('municipios', () => this.medicoControleAdministrativoService.consultarMedicosPorMunicipio(uf, this.limiteRanking), requestId);
        this.carregarGrupo('transferencias-origem-destino', () => this.medicoControleAdministrativoService.consultarTransferenciasOrigemDestino(uf, 30), requestId);
        this.carregarGrupo('movimentacao-situacao', () => this.medicoControleAdministrativoService.consultarMovimentacaoSituacaoCadastral(uf, this.anoSelecionado, this.limiteRanking), requestId);
        this.carregarGrupo('tempo-situacao', () => this.medicoControleAdministrativoService.consultarTempoMedioSituacaoCadastral(uf, this.limiteRanking), requestId);
        this.carregarGrupo('cancelamentos-situacao', () => this.medicoControleAdministrativoService.consultarCancelamentosPorSituacao(uf, this.anoSelecionado, this.limiteRanking), requestId);
        this.carregarGrupo('situacao-cadastral', () => this.medicoControleAdministrativoService.consultarDistribuicaoPorSituacao(uf), requestId);
        this.carregarGrupo('situacao-por-mes', () => this.medicoControleAdministrativoService.consultarSituacaoPorMes(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('reinscricoes-cancelamento', () => this.medicoControleAdministrativoService.consultarReinscricoesAposCancelamento(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('protocolos-medico', () => this.medicoControleAdministrativoService.consultarProtocolosMedicoPorTipo(uf, this.anoSelecionado, this.limiteRanking), requestId);
        this.carregarGrupo('emissoes-documentais', () => this.medicoControleAdministrativoService.consultarEmissoesDocumentais(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('documentos-ausentes', () => this.medicoControleAdministrativoService.consultarDocumentosAusentes(uf), requestId);
        this.carregarGrupo('cadastros-atualizacao', () => this.medicoControleAdministrativoService.consultarCadastrosPorAtualizacao(uf), requestId);
        this.carregarGrupo('cadastros-atualizados-recentes', () => this.medicoControleAdministrativoService.consultarCadastrosAtualizadosPorMes(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('anotacoes-administrativas', () => this.medicoControleAdministrativoService.consultarAnotacoesAdministrativas(uf, this.anoSelecionado), requestId);
        this.carregarGrupo('contatos-validos', () => this.medicoControleAdministrativoService.consultarContatosValidos(uf), requestId);
        this.carregarGrupo('duplicidades-potenciais', () => this.medicoControleAdministrativoService.consultarDuplicidadesPotenciais(uf), requestId);
        this.carregarGrupo('faculdades-formacao', () => this.medicoControleAdministrativoService.consultarRankingFaculdadesFormacao(uf, this.limiteRanking), requestId);
        this.carregarGrupo('sem-especialidade', () => this.medicoControleAdministrativoService.consultarMedicosSemEspecialidade(uf), requestId);
        this.carregarGrupo('media-especialidades', () => this.medicoControleAdministrativoService.consultarMediaEspecialidadesPorMedico(uf), requestId);
        this.carregarGrupo('subgrupos-especiais', () => this.medicoControleAdministrativoService.consultarSubgruposEspeciais(uf), requestId);
        this.carregarGrupo('formados-exterior', () => this.medicoControleAdministrativoService.consultarFormadosExteriorPorPais(uf, this.limiteRanking), requestId);
        this.carregarGrupo('especialidades', () => this.medicoControleAdministrativoService.consultarEspecialidadesMaisRegistradas(uf, this.limiteRanking), requestId);
        this.carregarGrupo('telemedicina-tipos', () => this.medicoControleAdministrativoService.consultarTelemedicinaTipos(uf), requestId);
        this.carregarGrupo('deficiencia-tipos', () => this.medicoControleAdministrativoService.consultarDeficienciaTipos(uf), requestId);
        this.carregarGrupo('pos-graduacao-tipos', () => this.medicoControleAdministrativoService.consultarPosGraduacaoTipos(uf), requestId);
        this.carregarGrupo('pos-graduacao-instituicoes', () => this.medicoControleAdministrativoService.consultarPosGraduacaoInstituicoes(uf, this.limiteRanking), requestId);
        this.carregarGrupo('conflito-interesse-situacao', () => this.medicoControleAdministrativoService.consultarConflitoInteresseSituacao(uf), requestId);
        this.carregarGrupo('pcmso-riscos', () => this.medicoControleAdministrativoService.consultarPcmsoRiscos(uf, this.limiteRanking), requestId);
        this.carregarGrupo('vistos-vencimento-faixa', () => this.medicoControleAdministrativoService.consultarVistosVencimentoFaixa(uf), requestId);
        this.carregarGrupo('projecao-aposentadoria', () => this.medicoControleAdministrativoService.consultarProjecaoAposentadoria(uf), requestId);
        this.carregarGrupo('retencao-formados', () => this.medicoControleAdministrativoService.consultarRetencaoFormados(uf), requestId);
        this.carregarGrupo('idade-media-especialidade', () => this.medicoControleAdministrativoService.consultarIdadeMediaEspecialidade(uf, this.limiteRanking), requestId);
        this.carregarGrupo('tempo-inscricao-titulo', () => this.medicoControleAdministrativoService.consultarTempoInscricaoTitulo(uf, this.limiteRanking), requestId);
        this.carregarGrupo('evolucao-anual-ativos', () => this.medicoControleAdministrativoService.consultarEvolucaoAnualAtivos(uf), requestId);
        this.carregarGrupo('interiorizacao-anual', () => this.medicoControleAdministrativoService.consultarInteriorizacaoAnual(uf), requestId);
        this.carregarGrupo('tempo-medio-atendimento-protocolo', () => this.medicoControleAdministrativoService.consultarTempoMedioAtendimentoProtocolo(uf, this.anoSelecionado, this.limiteRanking), requestId);
        this.carregarGrupo('tempo-medio-tramitacao-transferencia', () => this.medicoControleAdministrativoService.consultarTempoMedioTramitacaoTransferencia(uf), requestId);
        this.carregarGrupo('isencoes-doenca', () => this.medicoControleAdministrativoService.consultarIsencoesPorMotivo(uf), requestId);
        this.carregarGrupo('termo-lgpd', () => this.medicoControleAdministrativoService.consultarTermoLgpd(uf), requestId);
        this.carregarGrupo('vistos-concedidos-tipo', () => this.medicoControleAdministrativoService.consultarVistosConcedidosPorTipo(uf), requestId);
        this.carregarGrupo('ocorrencias-tipo', () => this.medicoControleAdministrativoService.consultarOcorrenciasPorTipo(uf, this.limiteRanking), requestId);
        this.carregarGrupo('diplomas-apresentacao', () => this.medicoControleAdministrativoService.consultarDiplomasApresentacao(uf), requestId);
        this.carregarGrupo('estado-civil', () => this.medicoControleAdministrativoService.consultarEstadoCivil(uf), requestId);
        this.carregarGrupo('tipo-sanguineo', () => this.medicoControleAdministrativoService.consultarTipoSanguineo(uf, this.limiteRanking), requestId);
    }

    private carregarAnaliticoEstatistica(): void {
        if (!this.estatisticaSelecionada || !this.grupoAnaliticoCodigo) {
            return;
        }

        const estatisticaId = this.estatisticaSelecionada.id;
        const codigo = this.grupoAnaliticoCodigo;
        const { page, size } = this.obterPaginacaoAnalitica();

        this.carregandoAnalitico = true;
        this.medicoControleAdministrativoService
            .consultarMedicosEstatisticaCadastro(estatisticaId, this.ufConsulta, this.anoSelecionado, page, size, codigo)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.analiticoChange$),
                // markForCheck: estado assíncrono gravado em campos não-signal; sob o layout OnPush o
                // tick da resposta HTTP não alcança este painel (só renderizava após um clique).
                finalize(() => {
                    if (this.estatisticaSelecionada?.id === estatisticaId && this.grupoAnaliticoCodigo === codigo) {
                        this.carregandoAnalitico = false;
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (resp) => {
                    if (this.estatisticaSelecionada?.id !== estatisticaId || this.grupoAnaliticoCodigo !== codigo) {
                        return;
                    }

                    this.linhasAnalitico = (resp?.content ?? []).map((item) => this.mapLinhaAnalitica(item));
                    this.totalRegistrosAnalitico = resp?.totalElements ?? 0;
                },
                error: () => {
                    if (this.estatisticaSelecionada?.id !== estatisticaId || this.grupoAnaliticoCodigo !== codigo) {
                        return;
                    }

                    this.linhasAnalitico = [];
                    this.totalRegistrosAnalitico = 0;
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar o relatório analítico da estatística.' });
                }
            });
    }

    private carregarGrupo(id: EstatisticaId, consulta: () => Observable<MedicoEstatisticaAgrupadaResponse[]>, requestId: number): void {
        if (!this.pertenceAoAgrupadorSelecionado(id)) {
            this.definirCarregando(id, false);
            return;
        }

        consulta()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.ufChange$),
                finalize(() => {
                    if (this.isRequestAtual(requestId)) {
                        this.definirCarregando(id, false);
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (itens) => {
                    const normalizados = this.normalizarItens(itens);
                    const finais = ESTATISTICAS_MENSAIS.has(id) ? this.preencherMesesVazios(normalizados) : normalizados;
                    this.atualizarGrupo(id, finais, requestId);
                },
                error: () => this.atualizarGrupo(id, [], requestId)
            });
    }

    private obterCodigoFiltroAnalitico(): string | null {
        return this.grupoAnaliticoCodigo;
    }

    private obterGrupoAnaliticoSelecionado(): MedicoEstatisticaAgrupadaResponse | null {
        if (!this.estatisticaSelecionada || !this.grupoAnaliticoCodigo) {
            return null;
        }

        return this.obterOpcoesGrupoAnalitico(this.estatisticaSelecionada).find((item) => item.codigo === this.grupoAnaliticoCodigo) ?? null;
    }

    private normalizarFaixaEtaria(valor: string | null | undefined): FaixaEtariaCodigo {
        return this.faixaEtariaOpcoes.find((opcao) => opcao.codigo === valor)?.codigo ?? this.faixaEtariaPadrao;
    }

    private normalizarSubgrupoEspecial(valor: string | null | undefined): SubgrupoEspecialCodigo {
        return this.subgrupoEspecialOpcoes.find((opcao) => opcao.codigo === valor)?.codigo ?? this.subgrupoEspecialPadrao;
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        const ufTokenValida = /^[A-Z]{2}$/.test(ufToken) ? ufToken : '';

        if (ufTokenValida && ufTokenValida !== 'BR') {
            this.ufTokenRestrita = ufTokenValida;
            this.ufsPermitidas = [ufTokenValida];
            this.ufSelecionada = ufTokenValida;
            this.ufSelecaoGestao.definir(ufTokenValida);
            return;
        }

        this.ufTokenRestrita = null;
        this.ufsPermitidas = null;
        this.ufSelecionada = this.ufSelecaoGestao.uf();
    }

    private normalizarUfSelecionada(uf?: string | null): string | null {
        const ufNormalizada = String(uf ?? '')
            .trim()
            .toUpperCase();

        if (!ufNormalizada || ufNormalizada === 'BR') {
            return this.ufTokenRestrita;
        }

        if (!/^[A-Z]{2}$/.test(ufNormalizada)) {
            return this.ufTokenRestrita;
        }

        if (this.ufTokenRestrita && ufNormalizada !== this.ufTokenRestrita) {
            return this.ufTokenRestrita;
        }

        return ufNormalizada;
    }

    private normalizarAno(valor: number): number {
        const ano = Number(valor);

        if (!Number.isFinite(ano)) {
            return this.anoAtual;
        }

        return Math.min(Math.max(Math.trunc(ano), 1900), this.anoAtual + 1);
    }

    private normalizarItens(itens: MedicoEstatisticaAgrupadaResponse[] | null | undefined): MedicoEstatisticaAgrupadaResponse[] {
        return (itens ?? [])
            .map((item) => ({
                ...item,
                codigo: item.codigo?.trim() || null,
                descricao: item.descricao?.trim() || item.codigo?.trim() || 'Não informado',
                ufSgUf: item.ufSgUf?.trim() || null,
                ordem: item.ordem ?? null,
                total: item.total ?? 0
            }))
            .sort((a, b) => {
                const ordemA = a.ordem ?? Number.MAX_SAFE_INTEGER;
                const ordemB = b.ordem ?? Number.MAX_SAFE_INTEGER;

                if (ordemA !== ordemB) {
                    return ordemA - ordemB;
                }

                return (b.total ?? 0) - (a.total ?? 0);
            });
    }

    /**
     * A API só devolve linha para o mês que teve movimento. Sem completar os meses zerados,
     * a série mensal termina no último mês com dado e a linha de tendência engana.
     */
    private preencherMesesVazios(itens: MedicoEstatisticaAgrupadaResponse[]): MedicoEstatisticaAgrupadaResponse[] {
        const ano = this.anoSelecionado;
        const ultimoMes = ano === this.anoAtual ? new Date().getMonth() + 1 : 12;
        const porCodigo = new Map(itens.map((item) => [item.codigo ?? '', item]));

        const meses: MedicoEstatisticaAgrupadaResponse[] = [];
        for (let mes = 1; mes <= ultimoMes; mes++) {
            const mesFormatado = String(mes).padStart(2, '0');
            const codigo = `${ano}-${mesFormatado}`;
            meses.push(
                porCodigo.get(codigo) ?? {
                    codigo,
                    descricao: `${mesFormatado}/${ano}`,
                    ufSgUf: null,
                    ordem: Number(`${ano}${mesFormatado}`),
                    total: 0
                }
            );
        }

        // Preserva linhas com código fora do padrão AAAA-MM, se houver.
        const foraDoPadrao = itens.filter((item) => !/^\d{4}-\d{2}$/.test(item.codigo ?? ''));
        return [...meses, ...foraDoPadrao];
    }

    private resetarGrupos(): void {
        this.estatisticas = this.estatisticas.map((grupo) => ({
            ...grupo,
            itens:
                grupo.id === 'limite-gastos-mensal'
                    ? [
                          { codigo: 'Gasto', descricao: 'Valor Gasto', total: 7243 },
                          { codigo: 'Restante', descricao: 'Valor Restante', total: 2757 }
                      ]
                    : null,
            carregando: grupo.id === 'limite-gastos-mensal' ? false : true
        }));
    }

    private atualizarGrupo(id: EstatisticaId, itens: MedicoEstatisticaAgrupadaResponse[], requestId: number): void {
        if (!this.isRequestAtual(requestId)) {
            return;
        }

        let itensFiltrados = id === 'transferencias-origem-destino' ? itens.filter((item) => UFS_VALIDAS.has(this.obterUfTransferencia(item))) : itens;

        if (ESTATISTICAS_MENSAIS.has(id)) {
            itensFiltrados = EstatisticaMensalUtils.preencherMesesAusentes(itensFiltrados);
        }

        this.estatisticas = this.estatisticas.map((grupo) =>
            grupo.id === id
                ? {
                      ...grupo,
                      itens: itensFiltrados,
                      carregando: false
                  }
                : grupo
        );
    }

    private definirCarregando(id: EstatisticaId, carregando: boolean): void {
        this.estatisticas = this.estatisticas.map((grupo) => (grupo.id === id ? { ...grupo, carregando } : grupo));
    }

    private obterPaginacaoAnalitica(): { page: number; size: number } {
        const size = this.rowsAnalitico;
        const page = size > 0 ? Math.floor(this.firstAnalitico / size) : 0;
        return { page, size };
    }

    private mapLinhaAnalitica(item: MedicoPendenciaResponse): PendenciaLinha {
        return {
            uf: item.ufSgUf ?? '',
            crm: item.nuCrm ?? '',
            nome: item.nmSolicitante ?? '',
            situacao: item.dsSituacao ?? item.inSituacao ?? '',
            codigoSituacao: item.inSituacao ?? '',
            detalhe: item.informacaoComplementar ?? ''
        };
    }

    private obterMaiorTotal(grupo: EstatisticaGrupo): number {
        return Math.max(0, ...(grupo.itens ?? []).map((item) => item.total ?? 0));
    }

    private iniciarAtualizacao(): number {
        this.atualizacaoId += 1;
        return this.atualizacaoId;
    }

    private isRequestAtual(requestId: number): boolean {
        return requestId === this.atualizacaoId;
    }

    private observarAgrupadorRota(): void {
        this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            const slug = params.get('grupo') ?? this.agrupadores[0].slug;
            this.grupoSelecionadoSlug = this.isAgrupadorSlugValido(slug) ? slug : this.agrupadores[0].slug;
            this.grupoMaximizado = null;
            this.exibirModalMaximizados = false;
            this.voltarParaEstatisticas();
            // Cada aba carrega apenas as suas consultas; trocar de aba busca as da aba nova.
            this.atualizarEstatisticas();
            this.cdr.markForCheck();
        });
    }

    /** Evita disparar as consultas dos outros nove agrupadores ao abrir uma aba. */
    private pertenceAoAgrupadorSelecionado(id: EstatisticaId): boolean {
        const estatistica = this.estatisticas.find((grupo) => grupo.id === id);
        return estatistica?.grupo === this.obterAgrupadorSelecionado().label;
    }

    private isAgrupadorSlugValido(slug: string): boolean {
        return this.agrupadores.some((agrupador) => agrupador.slug === slug);
    }
}
